// src/shared/services/userService.ts
import apiService from '@core/api/apiService';
import axios from 'axios';
import { setCredentials } from '@app/store/slices/authSlice';
import { setCompany, setUser } from '@app/store/slices/userSlice';
import { store } from '@app/store';
import { Company, User } from '@core/types';

// Cache to prevent duplicate info calls
let infoLoadingPromise: Promise<void> | null = null;
let infoLoaded = false;
let infoToken: string | null = null;
let lastInfoAttemptAt = Number.NEGATIVE_INFINITY;
const BACKGROUND_REFRESH_INTERVAL_MS = 60000;

type UserInfoOptions = { force?: boolean; background?: boolean };

export const fetchAndSetUserInfo = async (options: UserInfoOptions = {}): Promise<void> => {
  const token = localStorage.getItem('token');
  if (!token) return;

  if (infoToken !== token) {
    infoToken = token;
    infoLoaded = false;
    infoLoadingPromise = null;
    lastInfoAttemptAt = Number.NEGATIVE_INFINITY;
  }

  // Even explicit refreshes share the current request. Clearing this promise
  // on every focus event used to launch overlapping profile requests.
  if (infoLoadingPromise) {
    await infoLoadingPromise;
    return;
  }
  if (options.background && (
    !window.navigator.onLine || document.visibilityState !== 'visible'
    || Date.now() - lastInfoAttemptAt < BACKGROUND_REFRESH_INTERVAL_MS
  )) return;
  if (infoLoaded && !options.force) return;

  // Start loading
  lastInfoAttemptAt = Date.now();
  const request = (async () => {
    try {
      const response = await apiService.get<{
        user?: User;
        roles?: string[];
        role_titles?: string[];
        permissions?: string[];
        company?: Company;
      }>('/v1/user/info');

      // A response from a previous login must not restore that session or
      // replace the profile after logout/account changes.
      if (localStorage.getItem('token') !== token) return;

      const { user: userPayload, roles = [], role_titles = [], permissions = [], company } = response || {};

      // Update Redux store
      if (roles && permissions) {
        store.dispatch(
          setCredentials({
            token,
            roles: Array.isArray(roles) ? roles : [],
            roleTitles: Array.isArray(role_titles) ? role_titles : [],
            permissions: (Array.isArray(permissions) ? permissions : []) as any,
          })
        );
      }

      if (userPayload) {
        store.dispatch(setUser(userPayload as User));
        const fullName = [userPayload.name, userPayload.lastname].filter(Boolean).join(' ').trim();
        if (fullName) localStorage.setItem('name', fullName);
        if (userPayload.mobile_number) localStorage.setItem('mobile', userPayload.mobile_number);
        if (userPayload.company_id !== undefined && userPayload.company_id !== null) {
          localStorage.setItem('company_id', String(userPayload.company_id));
        } else {
          localStorage.removeItem('company_id');
        }
      }

      if (company) {
        store.dispatch(setCompany(company));
      }

      infoLoaded = true;
    } catch (error) {
      if (localStorage.getItem('token') !== token) return;
      console.error('خطا در دریافت اطلاعات کاربر', error);
      // A timeout/502/503 is not proof that the token is invalid. Keep the
      // session so the dashboard can retry after the API becomes available;
      // only an explicit 401 should clear authentication state.
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
      }
      infoLoaded = false;
    }
  })();

  infoLoadingPromise = request;
  try {
    await request;
  } finally {
    if (infoLoadingPromise === request) infoLoadingPromise = null;
  }
};

/** Force refresh user/company profile (e.g. when opening profile modal). Bypasses cache. */
export const refreshUserInfo = async (options: { background?: boolean } = {}): Promise<void> => {
  await fetchAndSetUserInfo({ ...options, force: true });
};

export default fetchAndSetUserInfo;

